/**
 * UGC video carousel (sections/ugc-video-carousel.liquid)
 *
 * Mouse devices, detected with (hover: hover): hover plays after 150ms, click plays,
 * a second click pauses, mouse leave pauses in place.
 * Touch devices: the card snapped to the center is "active", autoplays muted and loops.
 * Reduced motion: no autoplay anywhere, click or tap to play.
 * Sound: once a shopper unmutes one video, the next one they play keeps sound on.
 *
 * Endless loop: the cards form a ring. If there aren't enough videos to fill the
 * screen, the set is repeated (clones). After every scroll settles, off-screen cards
 * are moved from one end to the other so the centered card always has cards on both
 * sides. Visible cards never move, so nothing jumps. Copies of the same video share
 * their playback position, so "resume where it paused" still holds.
 *
 * Note: written without "space + less-than" sequences to stay safe if this file is
 * ever inlined (layout/theme.liquid splits page output on that sequence).
 */
(function () {
  if (window.GSUgcCarousel) return;

  var HOVER_DELAY = 150;
  var SETTLE_DELAY = 150;
  var EXTRA_CARDS = 6; // ring slack beyond one screen, so fast clicks and flings never hit an end
  var soundOn = false; // shared across every carousel on the page

  var hoverQuery = window.matchMedia('(hover: hover)');
  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function Carousel(root) {
    this.root = root;
    this.track = root.querySelector('[data-ugc-track]');
    if (!this.track) return;

    this.originals = Array.prototype.slice.call(root.querySelectorAll('[data-ugc-card]'));
    this.prev = root.querySelector('[data-ugc-prev]');
    this.next = root.querySelector('[data-ugc-next]');
    this.loop = this.originals.length >= 2;
    this.activeCard = null;
    this.targetIndex = null;
    this.inView = false;
    this.touching = false;
    this.scrollTimer = null;
    this.times = {}; // playback position per video, shared by its copies
    this.observers = [];
    this.cleanups = [];

    if (this.loop) this.buildRing();
    this.cards = this.readCards();

    this.cards.forEach(this.bindCard, this);
    this.bindArrows();
    this.bindScroll();
    this.bindVisibility();

    // Start with the middle video centered, partial cards peeking on both sides
    var middle = this.originals[Math.floor((this.originals.length - 1) / 2)];
    this.centerCard(middle);
    this.rebalance();
    this.setActive(this.cards[this.closestIndex()]);
  }

  Carousel.prototype.isMouse = function () {
    return hoverQuery.matches;
  };

  Carousel.prototype.canAutoplay = function () {
    return !motionQuery.matches;
  };

  Carousel.prototype.on = function (el, type, fn, opts) {
    el.addEventListener(type, fn, opts);
    this.cleanups.push(function () { el.removeEventListener(type, fn, opts); });
  };

  Carousel.prototype.readCards = function () {
    return Array.prototype.slice.call(this.track.querySelectorAll('[data-ugc-card]'));
  };

  /* ---------- Ring ---------- */

  Carousel.prototype.step = function () {
    var a = this.originals[0];
    var b = this.originals[1];
    return b ? b.offsetLeft - a.offsetLeft : a.offsetWidth;
  };

  // Repeat the set until the ring is wide enough for the largest screen this device can show
  Carousel.prototype.buildRing = function () {
    var n = this.originals.length;
    var width = Math.max(this.track.clientWidth, window.screen ? window.screen.width : 0);
    var needed = Math.ceil(width / this.step()) + EXTRA_CARDS;
    var copies = Math.ceil(needed / n);

    for (var c = 1; copies > c; c++) {
      this.originals.forEach(function (card) {
        var clone = card.cloneNode(true);
        clone.setAttribute('data-ugc-clone', '');
        clone.removeAttribute('data-shopify-editor-block');
        clone.classList.remove('is-playing');
        this.track.appendChild(clone);
      }, this);
    }
  };

  Carousel.prototype.centerCard = function (card, smooth) {
    if (!card) return;
    var left = card.offsetLeft + card.offsetWidth / 2 - this.track.clientWidth / 2;
    if (smooth) {
      this.track.scrollTo({ left: Math.max(0, left), behavior: this.canAutoplay() ? 'smooth' : 'auto' });
    } else {
      this.track.scrollLeft = Math.max(0, left);
    }
  };

  // Move off-screen cards between the ends so the centered card sits in the middle of the ring.
  // The centered card is re-centered from its own position, so the move is invisible.
  Carousel.prototype.rebalance = function () {
    if (!this.loop) return;
    var cards = this.readCards();
    var center = cards[this.closestIndex(cards)];
    var current = cards.indexOf(center);
    var wanted = Math.floor((cards.length - 1) / 2);
    var moves = current - wanted;
    if (moves === 0) return;

    var i;
    if (moves > 0) {
      for (i = 0; i !== moves; i++) this.track.appendChild(cards[i]);
    } else {
      for (i = 0; i !== -moves; i++) this.track.insertBefore(cards[cards.length - 1 - i], this.track.firstElementChild);
    }
    this.cards = this.readCards();
    this.centerCard(center);
  };

  /* ---------- Playback ---------- */

  Carousel.prototype.videoOf = function (card) {
    return card ? card.querySelector('[data-ugc-video]') : null;
  };

  Carousel.prototype.keyOf = function (card) {
    return card.getAttribute('data-index');
  };

  Carousel.prototype.play = function (card) {
    var video = this.videoOf(card);
    if (!video) return;

    // Only one video plays at a time
    this.cards.forEach(function (other) {
      if (other !== card) this.pause(other);
    }, this);

    // A copy of this video may have played further; pick up from there
    var saved = this.times[this.keyOf(card)];
    if (saved != null && Math.abs(video.currentTime - saved) > 0.5) {
      try { video.currentTime = saved; } catch (e) { /* not seekable yet */ }
    }

    var wantSound = soundOn && card.hasAttribute('data-has-mute');
    video.muted = !wantSound;

    var attempt = video.play();
    if (attempt && attempt.catch) {
      attempt.catch(function () {
        // Sound blocked: retry muted. Autoplay blocked entirely (e.g. iOS Low Power
        // Mode): the poster and play icon stay up and a tap plays it.
        if (!video.muted) {
          video.muted = true;
          video.play().catch(function () {});
        }
      });
    }
  };

  Carousel.prototype.pause = function (card) {
    var video = this.videoOf(card);
    if (video && !video.paused) video.pause();
  };

  Carousel.prototype.toggle = function (card) {
    var video = this.videoOf(card);
    if (!video) return;
    if (video.paused) {
      card._userPaused = false;
      this.play(card);
    } else {
      card._userPaused = true;
      this.pause(card);
    }
  };

  Carousel.prototype.syncMuteButton = function (card) {
    var btn = card.querySelector('[data-ugc-mute]');
    var video = this.videoOf(card);
    if (!btn || !video) return;
    var unmuted = !video.muted;
    btn.classList.toggle('is-unmuted', unmuted);
    btn.setAttribute('aria-pressed', unmuted ? 'true' : 'false');
    btn.setAttribute('aria-label', unmuted ? 'Mute video' : 'Unmute video');
  };

  /* ---------- Cards ---------- */

  Carousel.prototype.bindCard = function (card) {
    var self = this;
    var media = card.querySelector('[data-ugc-media]');
    var video = this.videoOf(card);
    var muteBtn = card.querySelector('[data-ugc-mute]');
    var hoverTimer = null;

    if (!media || !video) return;

    this.on(video, 'play', function () { card.classList.add('is-playing'); });
    this.on(video, 'pause', function () {
      card.classList.remove('is-playing');
      self.times[self.keyOf(card)] = video.currentTime;
    });
    this.on(video, 'volumechange', function () { self.syncMuteButton(card); });

    // Mouse: hover to play after a short delay, pause where it is on leave
    this.on(media, 'mouseenter', function () {
      if (!self.isMouse() || !self.canAutoplay()) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function () {
        if (!card._userPaused) self.play(card);
      }, HOVER_DELAY);
    });
    this.on(media, 'mouseleave', function () {
      if (!self.isMouse()) return;
      clearTimeout(hoverTimer);
      card._userPaused = false;
      self.pause(card);
    });

    this.on(media, 'click', function (e) {
      if (e.target.closest('[data-ugc-mute]')) return;
      clearTimeout(hoverTimer);

      if (self.isMouse()) {
        self.toggle(card);
        return;
      }

      // Touch: tapping a peeking card brings it to the center (it becomes active)
      if (card !== self.activeCard) {
        self.centerCard(card, true);
        return;
      }
      self.toggle(card);
    });

    this.on(media, 'keydown', function (e) {
      if (e.target !== media) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        self.toggle(card);
      }
    });

    if (muteBtn) {
      this.on(muteBtn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var nowMuted = !video.muted;
        video.muted = nowMuted;
        soundOn = !nowMuted;
        if (!nowMuted && video.paused) {
          card._userPaused = false;
          self.play(card);
        }
      });
    }
  };

  /* ---------- Scrolling + active card ---------- */

  Carousel.prototype.closestIndex = function (cards) {
    cards = cards || this.cards;
    var center = this.track.scrollLeft + this.track.clientWidth / 2;
    var best = 0;
    var bestDist = Infinity;
    cards.forEach(function (card, i) {
      var dist = Math.abs(card.offsetLeft + card.offsetWidth / 2 - center);
      if (dist >= bestDist) return;
      best = i;
      bestDist = dist;
    });
    return best;
  };

  Carousel.prototype.setActive = function (card) {
    if (card === this.activeCard) return;
    var prevCard = this.activeCard;
    this.activeCard = card;

    if (this.isMouse()) return;

    if (prevCard) {
      prevCard._userPaused = false;
      this.pause(prevCard);
    }
    if (card) card._userPaused = false;
    this.autoplayActive();
  };

  // Touch only: play the active card if allowed. Resumes from where it paused.
  Carousel.prototype.autoplayActive = function () {
    if (this.isMouse() || !this.canAutoplay() || !this.inView) return;
    var card = this.activeCard;
    if (card && !card._userPaused) this.play(card);
  };

  Carousel.prototype.bindArrows = function () {
    var self = this;
    // Step from the card we're heading to, so fast clicks during a smooth scroll aren't lost
    function go(dir) {
      var from = self.targetIndex != null ? self.targetIndex : self.closestIndex();
      var to = Math.max(0, Math.min(self.cards.length - 1, from + dir));
      self.targetIndex = to;
      self.centerCard(self.cards[to], true);
    }
    if (this.prev) this.on(this.prev, 'click', function () { go(-1); });
    if (this.next) this.on(this.next, 'click', function () { go(1); });
  };

  Carousel.prototype.bindScroll = function () {
    var self = this;

    function settle() {
      // Never rearrange cards under a finger that's still on the screen
      if (self.touching) return;
      self.targetIndex = null;
      self.rebalance();
      self.setActive(self.cards[self.closestIndex()]);
    }

    function schedule() {
      clearTimeout(self.scrollTimer);
      self.scrollTimer = setTimeout(settle, SETTLE_DELAY);
    }

    this.on(this.track, 'scroll', schedule, { passive: true });
    this.on(this.track, 'touchstart', function () { self.touching = true; }, { passive: true });
    this.on(this.track, 'touchend', function () { self.touching = false; schedule(); }, { passive: true });
    this.on(this.track, 'touchcancel', function () { self.touching = false; schedule(); }, { passive: true });
    this.on(window, 'resize', schedule);
  };

  /* ---------- Visibility ---------- */

  Carousel.prototype.bindVisibility = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) {
      this.inView = true;
      return;
    }

    // Any video that scrolls out of view pauses (vertically or out of the track)
    var cardObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) self.pause(entry.target.closest('[data-ugc-card]'));
      });
    }, { threshold: 0 });
    this.cards.forEach(function (card) {
      var media = card.querySelector('[data-ugc-media]');
      if (media) cardObserver.observe(media);
    });

    // Touch: autoplay the active card only while the carousel is on screen
    var sectionObserver = new IntersectionObserver(function (entries) {
      self.inView = entries[0].isIntersecting;
      if (self.inView) self.autoplayActive();
    }, { threshold: 0.5 });
    sectionObserver.observe(this.track);

    this.observers.push(cardObserver, sectionObserver);
  };

  Carousel.prototype.destroy = function () {
    clearTimeout(this.scrollTimer);
    this.observers.forEach(function (o) { o.disconnect(); });
    this.cleanups.forEach(function (fn) { fn(); });
    (this.cards || []).forEach(function (card) {
      var video = card.querySelector('[data-ugc-video]');
      if (video) video.pause();
    });
  };

  /* ---------- Add to cart (single-variant products) ---------- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ugc-add][data-variant-id]');
    if (!btn) return;
    e.preventDefault();
    if (btn.classList.contains('is-loading')) return;

    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(btn.getAttribute('data-variant-id'), 10), quantity: 1 }] })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart.');
          return data;
        });
      })
      .then(function () {
        if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
        if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
      })
      .catch(function (err) {
        window.alert(err.message);
      })
      .then(function () {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      });
  });

  /* ---------- Init + theme editor ---------- */

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-ugc-carousel]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root._ugc) return;
      root._ugc = new Carousel(root);
    });
  }

  function destroy(scope) {
    var roots = scope.querySelectorAll('[data-ugc-carousel]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root._ugc && root._ugc.destroy) root._ugc.destroy();
      root._ugc = null;
    });
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroy(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest('[data-ugc-carousel]');
    if (!root || !root._ugc || !root._ugc.cards) return;
    root._ugc.centerCard(e.target, true);
  });

  window.GSUgcCarousel = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
})();
